import { useEffect, useState } from 'react'

import { apiClient, filesApi } from '@/lib/api'

/**
 * An object URL for a blob served behind the session by
 * `GET /v1/files/*path`.
 *
 * Page images cannot be handed to `<img src>` directly: the gateway serves
 * them authenticated, not through a signed storage URL, so the bytes have to
 * be fetched with the Authorization header and wrapped. The URL is revoked
 * on unmount and the fetch re-runs when the token changes, so an image does
 * not 401 forever after a refresh.
 */
export function useBlobUrl(path: string | undefined | null) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [authVersion, setAuthVersion] = useState(0)

  useEffect(() => {
    const unsubscribe = apiClient.onAuthVersionChange(setAuthVersion)
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!path) {
      setUrl(null)
      return
    }
    const controller = new AbortController()
    let objectUrl: string | null = null
    setError(null)
    // The previous page's URL is revoked by this effect's cleanup, so it must
    // stop being rendered first: switching pages otherwise paints a revoked
    // blob URL — a broken-image flash — until the new fetch resolves.
    setUrl(null)

    filesApi
      .objectUrl(path, controller.signal)
      .then((next) => {
        objectUrl = next
        setUrl(next)
      })
      .catch((cause: Error) => {
        if (!controller.signal.aborted) setError(cause)
      })

    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path, authVersion])

  return { url, error }
}
